import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QUICK_FIND } from './api';
import { quickFindKey } from './queryKeys';
import { isSearchable, normaliseTerm } from './term';
import { PORTAL_FAMILIES, type QuickFindGroup, type SearchPortal } from './types';

/**
 * The five states the overlay shows, each distinct (CONTRACT §4):
 *
 * - `initial`  nothing worth asking yet (under the minimum length);
 * - `loading`  the request for THIS term is in flight (the answer to the term
 *              before it, if any, is kept as `previous`: shown dimmed and busy,
 *              never as this term's answer and never to be chosen — QF-19);
 * - `results`  every family answered and something matched;
 * - `empty`    every family answered and nothing matched — the only "no results";
 * - `partial`  some family could not be searched; it is named, never hidden;
 * - `error`    the search itself failed, or no family could be searched.
 *   Never shown as "no results": nothing was looked at.
 */
const NO_GROUPS: QuickFindGroup[] = [];
const NO_FAMILIES: string[] = [];

export type QuickFindState = 'initial' | 'loading' | 'results' | 'empty' | 'partial' | 'error';

export interface QuickFindView {
  state: QuickFindState;
  /** The normalised term the state is about. */
  term: string;
  /** The families that answered, in the portal's order (known families only). */
  groups: QuickFindGroup[];
  /** The families that could not be searched just now. */
  unavailable: string[];
  /** The failure, for the session check (a 401 ends the overlay) and a 429. */
  error: unknown;
  /**
   * While loading: the previous term's answer, and that term — kept on screen,
   * dimmed and busy, so the list does not blank between two terms (QF-19).
   */
  previous: { term: string; groups: QuickFindGroup[] } | null;
  retry: () => void;
}

/**
 * One Quick Find request per settled term, keyed by WHO is searching.
 *
 * `term` is what the input settled on (the input debounces). The key holds the
 * portal, the identity and the term, so a slow answer to an earlier term can
 * never be shown for a later one — the last request wins — and TanStack aborts
 * the superseded request (its `signal` is forwarded to `fetch`). Results are
 * never retried silently, never persisted and kept only briefly; the overlay
 * removes them when it closes.
 */
export function useQuickFind({
  portal,
  identity,
  term: raw,
  enabled,
}: {
  portal: SearchPortal;
  identity: string;
  term: string;
  enabled: boolean;
}): QuickFindView {
  const term = normaliseTerm(raw);
  const searchable = isSearchable(term);

  const query = useQuery({
    queryKey: quickFindKey(portal, identity, term),
    queryFn: ({ signal }) => QUICK_FIND[portal]({ q: term, signal }),
    enabled: enabled && searchable,
    staleTime: 30_000,
    gcTime: 30_000,
    retry: false,
    // QF-19: the previous term's answer stays while the next is asked; it is
    // reported as `previous`, never as this term's answer.
    placeholderData: keepPreviousData,
    // Titles are bilingual pairs and statuses codes rendered locally: a
    // language switch needs no new request (EX-10).
    meta: { localeRefetch: false },
  });

  const retry = () => {
    void query.refetch();
  };
  const base = {
    term,
    groups: NO_GROUPS,
    unavailable: NO_FAMILIES,
    error: query.error,
    previous: null,
    retry,
  };

  // Derived once per answer, so a row's position survives re-renders.
  const { answered, groups, unavailable } = useMemo(() => {
    const known: readonly string[] = PORTAL_FAMILIES[portal];
    const answered = (query.data?.groups ?? [])
      .filter((group) => known.includes(group.type))
      .sort((a, b) => known.indexOf(a.type) - known.indexOf(b.type));

    return {
      answered,
      groups: answered.filter((group) => group.status === 'ok'),
      unavailable: answered.filter((group) => group.status !== 'ok').map((group) => group.type),
    };
  }, [query.data, portal]);
  const settled = { ...base, groups, unavailable };

  if (!searchable) return { ...base, state: 'initial' };

  if (query.isPlaceholderData) {
    return {
      ...base,
      state: 'loading',
      previous: groups.length > 0 ? { term: query.data?.q ?? '', groups } : null,
    };
  }

  if (query.isFetching && (query.data === undefined || query.isError || unavailable.length > 0))
    return { ...base, state: 'loading' };
  if (query.isError) return { ...base, state: 'error' };
  if (query.data === undefined) return { ...base, state: 'loading' };
  if (answered.length > 0 && groups.length === 0) return { ...settled, state: 'error' };
  if (unavailable.length > 0) return { ...settled, state: 'partial' };
  if (groups.every((group) => group.items.length === 0)) return { ...settled, state: 'empty' };

  return { ...settled, state: 'results' };
}
